import type {
  PlantAlarmAckFilter,
  PlantAlarmStatusFilter,
  PlantEntityStrayFilter,
  PlantEntityValidityFilter
} from '@axilon-apps/domains-ontology-ui-state'

import type { AppContextEntry } from '../../chat-app-context'
import { describeEntity, describeFilters, formatCount, summarizeList } from '../../chat-app-context'

/**
 * Plant tabs are path-routed, so the route baseline already names the tab
 * and every `:param` / `?param` selection. Only the two tabs whose view
 * state never reaches the URL — Alarms filters and the Model label —
 * need a registration.
 */

/** An alarm row the expanded accordion is currently showing. */
export interface ExpandedAlarm {
  name: string
  /** Canonical `[Provider]Folder/Tag` path of the source tag. */
  tagPath?: string
  priority: string
  /** Live gateway state, when the live poll has it. */
  liveState?: string
  /** UDT instance the alarm hangs off, when it is not a standalone tag. */
  udtInstance?: string
  /** Node URN — the agent's tool-call anchor. */
  id: string
}

/** How many expanded alarms are named verbatim before falling back to a count. */
const MAX_NAMED_ALARMS = 5

export interface AlarmsAppContextInput {
  /** Grouped UDT tree vs the flat searchable table. */
  grouped: boolean
  statusFilter: PlantAlarmStatusFilter
  /** Only meaningful while `statusFilter` is `active`. */
  ackFilter: PlantAlarmAckFilter
  /** Empty set = every priority. */
  activePriorities: readonly string[]
  /** Flat-view search box, already lowercased/trimmed by the page. */
  search: string
  /** Alarms left after the active filters. */
  matchingAlarmCount: number
  /** Alarms configured in the graph before filtering. */
  totalAlarmCount: number
  /**
   * Alarm rows revealed by the expanded UDT definition / instance sections.
   * Expansion is local component state, so without this "tell me about this
   * alarm" has nothing to resolve against.
   */
  expandedAlarms: readonly ExpandedAlarm[]
}

function alarmLabel(alarm: ExpandedAlarm): string {
  return alarm.tagPath === undefined || alarm.tagPath === ''
    ? alarm.name
    : `${alarm.name} (${alarm.tagPath})`
}

export function buildAlarmsAppContextEntries(input: AlarmsAppContextInput): AppContextEntry[] {
  const entries: AppContextEntry[] = []

  // One expanded alarm is unambiguous deixis — spend the bytes on it.
  const [only] = input.expandedAlarms
  if (input.expandedAlarms.length === 1 && only !== undefined) {
    entries.push({
      key: 'Expanded alarm',
      value: describeEntity({
        name: only.name,
        fields: [
          ['priority', only.priority],
          ['live state', only.liveState],
          ['tag', only.tagPath],
          ['UDT instance', only.udtInstance]
        ],
        id: only.id
      })
    })
  } else if (input.expandedAlarms.length > 1) {
    entries.push({
      key: 'Expanded alarms',
      value: summarizeList(input.expandedAlarms.slice(0, MAX_NAMED_ALARMS).map(alarmLabel), {
        noun: 'alarm',
        total: input.expandedAlarms.length,
        max: MAX_NAMED_ALARMS
      })
    })
  }

  entries.push(
    {
      key: 'Alarm filters',
      value: describeFilters([
        ['status', input.statusFilter === 'all' ? null : input.statusFilter],
        [
          'acknowledgement',
          input.statusFilter === 'active' && input.ackFilter !== 'all' ? input.ackFilter : null
        ],
        ['priority', input.activePriorities.length > 0 ? input.activePriorities.join(', ') : null],
        ['search', input.search]
      ])
    },
    {
      key: 'Alarm view',
      value: `${input.grouped ? 'grouped by UDT' : 'flat table'} — ${formatCount(
        input.matchingAlarmCount,
        'alarm'
      )} of ${input.totalAlarmCount} shown`
    }
  )

  return entries
}

export interface ModelBrowserAppContextInput {
  /** Node label being browsed. */
  label: string
  /** Entities carrying that label after the filters. */
  total: number
  search: string
  validity: PlantEntityValidityFilter
  /** `all` when the label is stray-exempt. */
  stray: PlantEntityStrayFilter
  /** Raw min-confidence input, `''` when unset. */
  minConfidence: string
}

export function buildModelBrowserAppContextEntries(
  input: ModelBrowserAppContextInput
): AppContextEntry[] {
  return [
    // Sharpen the route-derived label: "Plant / Model" plus a node label
    // like `Compute` otherwise reads as the Plant Compute tab.
    { key: 'Page', value: 'Plant / Model (ontology schema browser)' },
    {
      // `:Label` is the Cypher form, so the value cannot be mistaken for a
      // page or tab name.
      key: 'Node label',
      value: `:${input.label} — ${formatCount(input.total, 'entity', 'entities')} carry this label`
    },
    {
      key: 'Model filters',
      value: describeFilters([
        ['search', input.search.trim()],
        ['validity', input.validity === 'all' ? null : input.validity],
        ['strays', input.stray === 'stray' ? 'only' : null],
        ['min confidence', input.minConfidence]
      ])
    }
  ]
}
